/**
 * Shared presentational bits for the "How it works" explainer.
 *
 * Client-facing surface, so the voice here is plain English first and the
 * mechanism second. Nothing in this folder reads the brain store: the page
 * teaches the rules using the real formulas rather than the live data, so a
 * demo mid-flight can never make the explanation contradict itself.
 */
import clsx from 'clsx';
import type { ReactNode } from 'react';

export type Tone = 'gold' | 'mint' | 'blood' | 'azure' | 'violet' | 'orange';

const TONE_TEXT: Record<Tone, string> = {
  gold: 'text-gold-bright',
  mint: 'text-mint',
  blood: 'text-blood',
  azure: 'text-azure',
  violet: 'text-violet',
  orange: 'text-orange',
};

const TONE_BORDER: Record<Tone, string> = {
  gold: 'border-gold-dim/40',
  mint: 'border-mint/40',
  blood: 'border-blood/40',
  azure: 'border-azure/40',
  violet: 'border-violet/40',
  orange: 'border-orange/40',
};

const TONE_FILL: Record<Tone, string> = {
  gold: 'bg-gold/10',
  mint: 'bg-mint/10',
  blood: 'bg-blood/10',
  azure: 'bg-azure/10',
  violet: 'bg-violet/10',
  orange: 'bg-orange/10',
};

/** A titled block of the page. */
export function Section({
  eyebrow,
  title,
  lede,
  children,
  id,
}: {
  eyebrow?: string;
  title: string;
  lede?: ReactNode;
  children: ReactNode;
  id?: string;
}) {
  return (
    <section className="mt-10 first:mt-0" id={id}>
      {eyebrow ? <p className="label-small-caps text-gold-bright">{eyebrow}</p> : null}
      <h2 className="heading-display text-2xl text-cream mt-1.5">{title}</h2>
      {lede ? <div className="font-serif text-cream-soft mt-2.5 max-w-3xl leading-relaxed">{lede}</div> : null}
      <div className="mt-5">{children}</div>
    </section>
  );
}

/**
 * One rule, stated the way a studio owner would say it, with the mechanism
 * underneath. `why` is the part clients actually ask about, so it is never
 * hidden behind a disclosure.
 */
export function RuleCard({
  title,
  rule,
  why,
  detail,
  tone = 'gold',
}: {
  title: string;
  rule: ReactNode;
  why?: ReactNode;
  detail?: ReactNode;
  tone?: Tone;
}) {
  return (
    <div className="panel-dark p-5">
      <div className="flex items-start gap-2.5">
        <span className={clsx('mt-1.5 h-2 w-2 shrink-0 rounded-full', TONE_FILL[tone], TONE_TEXT[tone])}>
          <span className={clsx('block h-2 w-2 rounded-full', tone === 'gold' ? 'bg-gold-bright' : '')} />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-inter text-sm font-semibold text-cream">{title}</h3>
          <p className="font-serif text-cream-soft mt-1.5 leading-relaxed">{rule}</p>
          {why ? (
            <p className="font-serif text-cream-faint mt-2.5 text-sm leading-relaxed">
              <span className="label-small-caps text-gold-bright/80">Why </span>
              {why}
            </p>
          ) : null}
          {detail ? <div className="mt-3">{detail}</div> : null}
        </div>
      </div>
    </div>
  );
}

/** A rule expressed as arithmetic, for the people who want to see it. */
export function Formula({ children, label }: { children: ReactNode; label?: string }) {
  return (
    <div className="rounded-md border border-gold-dim/25 bg-ink-dark/60 px-4 py-3">
      {label ? <p className="label-small-caps text-cream-faint mb-1.5">{label}</p> : null}
      <pre className="data-mono text-[12.5px] leading-relaxed text-cream-soft whitespace-pre-wrap">{children}</pre>
    </div>
  );
}

export function Callout({ tone = 'gold', title, children }: { tone?: Tone; title?: string; children: ReactNode }) {
  return (
    <div className={clsx('rounded-lg border px-4 py-3.5', TONE_BORDER[tone], TONE_FILL[tone])}>
      {title ? <p className={clsx('label-small-caps', TONE_TEXT[tone])}>{title}</p> : null}
      <div className={clsx('font-serif text-cream-soft leading-relaxed', title ? 'mt-1.5' : '')}>{children}</div>
    </div>
  );
}

/** Label / value pair used across the calculators. */
export function Stat({
  label,
  value,
  caption,
  tone,
  strong,
}: {
  label: string;
  value: string;
  caption?: string;
  tone?: Tone;
  strong?: boolean;
}) {
  return (
    <div
      className="rounded-md border border-gold-dim/20 bg-ink-black px-3.5 py-3"
      data-testid={`stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <p className="label-small-caps text-cream-faint">{label}</p>
      <p
        className={clsx(
          'data-mono tabular-nums mt-1',
          strong ? 'text-[22px]' : 'text-[17px]',
          tone ? TONE_TEXT[tone] : 'text-cream',
        )}
      >
        {value}
      </p>
      {caption ? <p className="font-inter text-[11px] text-cream-faint mt-1 leading-snug">{caption}</p> : null}
    </div>
  );
}

/** Slider with a live readout. Used wherever a number is worth playing with. */
export function Slider({
  label,
  value,
  min,
  max,
  step = 1,
  onChange,
  format,
  hint,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (next: number) => void;
  format: (v: number) => string;
  hint?: string;
}) {
  return (
    <label className="block">
      <span className="flex items-baseline justify-between gap-3">
        <span className="label-small-caps text-cream-soft">{label}</span>
        <span className="data-mono tabular-nums text-sm text-gold-bright">{format(value)}</span>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-[rgb(var(--accent-gold-bright))]"
      />
      {hint ? <span className="block font-inter text-[11px] text-cream-faint mt-0.5">{hint}</span> : null}
    </label>
  );
}

/** Segmented control. Small enough that a shared Tabs component is not worth it. */
export function Segmented<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label?: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <div>
      {label ? <p className="label-small-caps text-cream-soft mb-2">{label}</p> : null}
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const active = o.value === value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onChange(o.value)}
              aria-pressed={active}
              className={clsx(
                'rounded-full border px-3.5 py-1.5 font-inter text-xs uppercase tracking-wider transition',
                active
                  ? 'border-gold-bright bg-gold/15 text-gold-bright'
                  : 'border-gold-dim/30 text-cream-soft hover:border-gold-dim/60 hover:text-cream',
              )}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Plain dropdown, styled to match. */
export function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: ReadonlyArray<{ value: T; label: string }>;
  onChange: (next: T) => void;
}) {
  return (
    <label className="block">
      <span className="label-small-caps text-cream-soft">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="mt-1.5 w-full rounded-md border border-gold-dim/30 bg-ink-black px-3 py-2 font-inter text-sm text-cream focus:border-gold-bright focus:outline-none"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

/** The frame every interactive widget sits in, so they read as a set. */
export function Playground({
  title,
  instruction,
  children,
  footnote,
}: {
  title: string;
  instruction: string;
  children: ReactNode;
  footnote?: ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gold-dim/30 bg-ink-deep p-5">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h3 className="heading-serif text-sm text-cream">{title}</h3>
        <span className="pill-gold">Have a play</span>
      </div>
      <p className="font-serif text-cream-soft mt-2 text-sm leading-relaxed">{instruction}</p>
      <div className="mt-4">{children}</div>
      {footnote ? (
        <p className="font-inter text-[11px] text-cream-faint mt-4 leading-relaxed border-t border-gold-dim/15 pt-3">
          {footnote}
        </p>
      ) : null}
    </div>
  );
}
